import React from 'react';
import { Typography } from '@rbx/ui';
import { useTranslation } from '@rbx/intl';
import { ReferenceMemberReturn } from '@modules/client';
import EngineMarkdown from '@modules/ui/markdown/components/EngineMarkdown';
import isTypeOptional from '@modules/utilities/pageBuild/isTypeOptional';
import throwError from '@modules/utilities/error';
import { useReferenceTypesData } from './ReferenceTypesDataProvider';
import ReferenceMemberType from './ReferenceMemberType';
import SpaceSuffixedColon from './SpaceSuffixedColon';
import useReferenceMemberReturnsTableStyles from './ReferenceMemberReturnsTable.styles';

interface ReferenceMemberReturnsTableProps {
  returns: ReferenceMemberReturn[] | Array<{ type: string }> | null;
}

function ReferenceMemberReturnsTable({ returns }: ReferenceMemberReturnsTableProps) {
  const { translate } = useTranslation();
  const { classes } = useReferenceMemberReturnsTableStyles();
  const { getTypeAliasDatum } = useReferenceTypesData();

  if (!returns || returns.length === 0) {
    return null;
  }

  // Combine all summaries (if any exist)
  const combinedSummary = returns
    .map((returnItem) => ('summary' in returnItem ? returnItem.summary : ''))
    .filter(Boolean)
    .join('\n\n');

  return (
    <div className={classes.container}>
      <div className={classes.returnType}>
        <Typography component='span' variant='subtitle2' className={classes.returnLabel}>
          {translate('Label.Returns') || 'Returns'}
        </Typography>
        <SpaceSuffixedColon engineRevampEnabled />
        {returns.map((returnItem, index) => {
          const { type } = returnItem;
          const typeDatum = getTypeAliasDatum(type);
          if (!typeDatum) {
            throwError(`No type data found for ${type}`);
            return null;
          }

          return (
            <React.Fragment key={type}>
              <ReferenceMemberType
                parameter={typeDatum}
                isOptional={isTypeOptional(type)}
                engineRevampEnabled
              />
              {index < returns.length - 1 && <span>, </span>}
            </React.Fragment>
          );
        })}
      </div>
      {combinedSummary && (
        <div className={classes.description}>
          <EngineMarkdown
            overrideParagraphStyle={{
              marginBottom: 0,
            }}>
            {combinedSummary}
          </EngineMarkdown>
        </div>
      )}
    </div>
  );
}

export default ReferenceMemberReturnsTable;
